import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from "axios";
import { env } from "@/config/env";
import { ROUTES } from "@/constants/routes";
import { isJwtExpired } from "@/lib/jwt";
import { endpoints } from "@/services/api/endpoints";
import { useAuthStore } from "@/store/useAuthStore";
import { ApiError } from "./errors";

export const http = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 11_000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const AUTH_PATHS = new Set<string>([
  endpoints.login,
  endpoints.refresh,
  endpoints.verify,
]);

function isAuthPath(url?: string) {
  if (!url) return false;
  return [...AUTH_PATHS].some((path) => url.includes(path));
}

http.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // Let the browser set multipart boundary for FormData uploads.
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (typeof config.headers.set === "function") {
      config.headers.set("Content-Type", false);
    } else {
      delete (config.headers as Record<string, unknown>)["Content-Type"];
    }
  }

  if (isAuthPath(config.url)) {
    return config;
  }

  const { accessToken, authExpiry, refreshSession } = useAuthStore.getState();
  let token = accessToken;

  if (token && isJwtExpired(authExpiry)) {
    token = await refreshSession();
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status ?? 0;
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const body = error.response?.data;

    if (
      status === 401 &&
      original &&
      !original._retry &&
      !isAuthPath(original.url) &&
      typeof window !== "undefined" &&
      window.location.pathname !== ROUTES.login
    ) {
      original._retry = true;
      const token = await useAuthStore.getState().refreshSession();
      if (token) {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${token}`;
        return http(original);
      }
      // refreshSession already redirected to login
      return Promise.reject(new ApiError(status, body, error.message));
    }

    return Promise.reject(new ApiError(status, body, error.message));
  },
);

export async function apiGet<T>(url: string, config?: AxiosRequestConfig) {
  const { data } = await http.get<T>(url, config);
  return data;
}

export async function apiPost<T, B = unknown>(
  url: string,
  payload?: B,
  config?: AxiosRequestConfig,
) {
  const { data } = await http.post<T>(url, payload, config);
  return data;
}

export async function apiPut<T, B = unknown>(
  url: string,
  payload?: B,
  config?: AxiosRequestConfig,
) {
  const { data } = await http.put<T>(url, payload, config);
  return data;
}

export async function apiPatch<T, B = unknown>(
  url: string,
  payload?: B,
  config?: AxiosRequestConfig,
) {
  const { data } = await http.patch<T>(url, payload, config);
  return data;
}

export async function apiDelete<T>(url: string, config?: AxiosRequestConfig) {
  const { data } = await http.delete<T>(url, config);
  return data;
}
